<template>
  <div class="bancada-genia">
    <b-container class="py-4 py-md-5">
      <!-- Hero card -->
      <div class="hero-card">
        <div class="hero-content">
          <div class="hero-icon">
            <i class="fa fa-map-signs"></i>
          </div>
          <div class="hero-text">
            <h1 class="hero-title">Bancada Previdenciária GenIA</h1>
            <p class="hero-subtitle">
              Comece pelo mapa para entender o fluxo, ou acesse uma especialista direto.
            </p>
          </div>
        </div>
        <button type="button" class="hero-btn" @click="scrollToFirstEtapa">
          <i class="fa fa-compass"></i>
          Quero ajuda para encontrar o caminho
        </button>
      </div>

      <!-- Timeline com etapas -->
      <div class="timeline">
        <div
          v-for="etapa in etapas"
          :key="etapa.numero"
          :id="`etapa-${etapa.numero}`"
          class="etapa-block"
        >
          <div class="etapa-header">
            <div class="etapa-num">{{ etapa.numero }}</div>
            <div class="etapa-info">
              <h3 class="etapa-title">{{ etapa.titulo }}</h3>
              <p class="etapa-desc">{{ etapa.descricao }}</p>
            </div>
          </div>

          <div class="genias-grid">
            <div
              v-for="genia in etapa.genias"
              :key="genia.id"
              :class="['genia-card', `cat-${genia.categoria}`]"
              tabindex="0"
              role="button"
              :aria-label="`Abrir chat com ${genia.nome}`"
              @click="onCardClick(genia)"
              @keypress.enter="onCardClick(genia)"
              @keypress.space.prevent="onCardClick(genia)"
            >
              <img
                v-if="!imageErrors[genia.id]"
                :src="genia.image"
                :alt="genia.nome"
                class="genia-image"
                @error="onImageError(genia.id)"
              />
              <div v-else class="genia-image-fallback">
                <i class="fa fa-comments"></i>
              </div>
              <div class="genia-overlay"></div>
              <span class="genia-tag">{{ categoryLabels[genia.categoria] }}</span>
              <div class="genia-content">
                <h4 class="genia-nome">{{ genia.nome }}</h4>
                <p class="genia-desc">{{ genia.descricao }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trilhas temáticas -->
      <section class="trilhas-section">
        <div class="trilhas-header">
          <h2 class="trilhas-title">Trilhas prontas para começar</h2>
          <p class="trilhas-subtitle">
            Escolha uma situação comum e veja por onde entrar.
          </p>
        </div>

        <div class="trilhas-grid">
          <div
            v-for="trilha in trilhas"
            :key="trilha.id"
            class="trilha-card"
          >
            <h4 class="trilha-titulo">"{{ trilha.titulo }}"</h4>
            <div class="trilha-passos">
              <template v-for="(passo, idx) in trilha.passos">
                <button
                  :key="`${trilha.id}-pill-${idx}`"
                  type="button"
                  class="trilha-pill"
                  :aria-label="`Abrir chat com ${passo.label}`"
                  @click="onPassoClick(passo)"
                >{{ passo.label }}</button>
                <span
                  v-if="idx < trilha.passos.length - 1"
                  :key="`${trilha.id}-sep-${idx}`"
                  class="trilha-arrow"
                  aria-hidden="true"
                >›</span>
              </template>
            </div>
            <button
              type="button"
              class="trilha-ver"
              :aria-label="`Ver trilha ${trilha.titulo}`"
              @click="onVerTrilhaClick(trilha)"
            >
              Ver trilha →
            </button>
          </div>
        </div>
      </section>
    </b-container>
  </div>
</template>

<script>
import { ETAPAS, CATEGORY_LABELS, TRILHAS } from '@/data/bancada';
import { chatBus } from '@/events/chatBus';

export default {
  name: 'BancadaGenIA',
  data() {
    return {
      etapas: ETAPAS,
      categoryLabels: CATEGORY_LABELS,
      trilhas: TRILHAS,
      imageErrors: {},
    };
  },
  methods: {
    onImageError(geniaId) {
      this.$set(this.imageErrors, geniaId, true);
    },
    onCardClick(genia) {
      chatBus.$emit('chat-open-agent', genia.id);
    },
    onPassoClick(passo) {
      chatBus.$emit('chat-open-agent', passo.agentId);
    },
    onVerTrilhaClick(trilha) {
      const primeiroPasso = trilha.passos && trilha.passos[0];
      if (primeiroPasso) {
        chatBus.$emit('chat-open-agent', primeiroPasso.agentId);
      }
    },
    scrollToFirstEtapa() {
      const target = document.getElementById('etapa-1');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
};
</script>

<style scoped>
.bancada-genia {
  min-height: calc(100vh - 56px);
  background-color: #f5f7fa;
}

/* ==================== Hero card ==================== */
.hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 2.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.hero-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.hero-icon {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: #0b6baf;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  flex-shrink: 0;
}

.hero-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #0b6baf;
  margin: 0 0 0.25rem 0;
}

.hero-subtitle {
  font-size: 0.95rem;
  color: #4a5568;
  margin: 0;
  max-width: 540px;
}

.hero-btn {
  background: #f59e0b;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.7rem 1.25rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.hero-btn:hover,
.hero-btn:focus {
  background: #d97706;
  outline: none;
}

@media (max-width: 575px) {
  .hero-card {
    flex-direction: column;
    align-items: stretch;
  }
  .hero-content {
    justify-content: flex-start;
  }
  .hero-btn {
    width: 100%;
    justify-content: center;
  }
}

/* ==================== Timeline ==================== */
.timeline {
  position: relative;
  padding-left: 0;
}

/* Linha vertical conectando os circulos das etapas (desktop/tablet) */
@media (min-width: 768px) {
  .timeline::before {
    content: '';
    position: absolute;
    top: 20px;
    bottom: 20px;
    left: 19px; /* alinhado com o centro do circulo (40 / 2 = 20) menos metade da espessura */
    width: 2px;
    background: #cbd5e1;
    z-index: 0;
  }
}

.etapa-block {
  position: relative;
  margin-bottom: 2.5rem;
  /* compensa navbar sticky (56px) + admin-subnav quando logado como admin */
  scroll-margin-top: 120px;
}

.etapa-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  position: relative;
  z-index: 1;
}

.etapa-num {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #0b6baf;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  box-shadow: 0 0 0 4px #f5f7fa; /* recorta a linha vertical no circulo */
}

.etapa-info {
  flex: 1;
}

.etapa-title {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #1a202c;
}

.etapa-desc {
  font-size: 0.9rem;
  color: #4a5568;
  margin: 0;
  margin-top: 2px;
}

/* ==================== Grid de cards ==================== */
.genias-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr;
  padding-left: 56px; /* alinha com o texto da etapa apos o circulo */
}

@media (min-width: 768px) {
  .genias-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 575px) {
  .genias-grid {
    padding-left: 0;
  }
}

/* ==================== Card estilo Lovable ==================== */
.genia-card {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  background: #1a202c;
  border: 2px solid transparent;
  aspect-ratio: 16 / 10;
  min-height: 260px;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.genia-card:hover,
.genia-card:focus {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.18);
  outline: none;
}

@media (max-width: 575px) {
  .genia-card {
    min-height: 240px;
  }
}

.genia-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.genia-image-fallback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 4rem;
  background: #2d3748;
}

/* Gradiente sobreposto: leve no topo, forte no rodape, garante leitura do texto */
.genia-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.35) 45%,
    rgba(0, 0, 0, 0.82) 100%
  );
  pointer-events: none;
}

/* Tag de categoria */
.genia-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 10px;
  border-radius: 4px;
  z-index: 1;
  color: #ffffff;
  backdrop-filter: blur(2px);
}

.cat-diagnostico .genia-tag {
  background: rgba(14, 165, 233, 0.9);
}
.cat-nucleo .genia-tag {
  background: rgba(30, 58, 138, 0.9);
}
.cat-aprofundamento .genia-tag {
  background: rgba(251, 146, 60, 0.95);
}
.cat-ponte .genia-tag {
  background: rgba(20, 184, 166, 0.9);
}
.cat-recuperacao .genia-tag {
  background: rgba(168, 85, 247, 0.9);
}
.cat-saida .genia-tag {
  background: rgba(34, 197, 94, 0.9);
}

/* Borda animada no hover por categoria */
.cat-diagnostico:hover,
.cat-diagnostico:focus { border-color: #0ea5e9; }
.cat-nucleo:hover,
.cat-nucleo:focus { border-color: #1e3a8a; }
.cat-aprofundamento:hover,
.cat-aprofundamento:focus { border-color: #fb923c; }
.cat-ponte:hover,
.cat-ponte:focus { border-color: #14b8a6; }
.cat-recuperacao:hover,
.cat-recuperacao:focus { border-color: #a855f7; }
.cat-saida:hover,
.cat-saida:focus { border-color: #22c55e; }

/* Conteudo de texto sobreposto (rodape do card) */
.genia-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem 1.25rem 1.1rem;
  z-index: 1;
  color: #ffffff;
}

.genia-nome {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.4rem 0;
  color: #ffffff;
  line-height: 1.25;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.genia-desc {
  font-size: 0.825rem;
  margin: 0;
  line-height: 1.4;
  background: rgba(0, 0, 0, 0.4);
  padding: 6px 10px;
  border-radius: 6px;
  backdrop-filter: blur(2px);
  display: inline-block;
}

/* ==================== Trilhas tematicas ==================== */
.trilhas-section {
  margin-top: 3.5rem;
}

.trilhas-header {
  margin-bottom: 1.25rem;
}

.trilhas-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.35rem 0;
  color: #1a202c;
}

.trilhas-subtitle {
  font-size: 0.95rem;
  color: #4a5568;
  margin: 0;
}

.trilhas-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
}

@media (min-width: 768px) {
  .trilhas-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1200px) {
  .trilhas-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.trilha-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem 1.1rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.15s, border-color 0.15s;
}

.trilha-card:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.trilha-titulo {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
  line-height: 1.35;
}

.trilha-passos {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.35rem;
}

.trilha-pill {
  background: #dbeafe;
  color: #1e3a8a;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color 0.12s, border-color 0.12s;
  line-height: 1.2;
}

.trilha-pill:hover,
.trilha-pill:focus {
  background: #bfdbfe;
  border-color: #1e3a8a;
  outline: none;
}

.trilha-arrow {
  color: #94a3b8;
  font-size: 1rem;
  line-height: 1;
  user-select: none;
}

.trilha-ver {
  align-self: flex-start;
  margin-top: 0.4rem;
  background: transparent;
  border: none;
  color: #f59e0b;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0;
  cursor: pointer;
  transition: color 0.12s;
}

.trilha-ver:hover,
.trilha-ver:focus {
  color: #d97706;
  text-decoration: underline;
  outline: none;
}
</style>
